import { usePatientMedicalEventAction } from './useActions'
import {
  Alert,
  Box,
  Grid2 as Grid,
  IconButton,
  Modal,
  styled,
  Tooltip,
  Typography
} from '@mui/material'
import InputBase from 'design-system/input/base'
import React from 'react'
import ButtonBase from 'design-system/button'
import { PATIENT_MEDICAL_EVENTS } from 'navigation/CONSTANTS'
import NavLinkBack from 'components/NavLinkBack'
import { useParams } from 'react-router-dom'
import SelectBox from 'components/SelectBox'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs from 'dayjs'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import claviendDindoImage from 'assets/clavien-dindo-info.png'

const StyledBox = styled(Box)(() => ({
  maxWidth: '1200px',
  padding: '20px 5% 90px 5%',
  width: '100%',
  justifyContent: 'center',
  alignSelf: 'center'
}))

const clavienDindoOptions = {
  I: 'Grau I',
  Id: 'Grau I - Deficiência',
  II: 'Grau II',
  IId: 'Grau II - Deficiência',
  IIIu: 'Grau III - Desfecho não definido',
  IIIa: 'Grau IIIa - Sem anestesia',
  IIIb: 'Grau IIIb - Com anestesia',
  IVu: 'Grau IV - Desfecho não definido',
  IVa: 'Grau IVa - Disfunção de orgão único',
  IVb: 'Grau IVb - Disfunção de múltiplos orgãos',
  V: 'Grau V'
}

const PatientMedicalEventActionPage: React.FC = () => {
  const {
    change,
    data,
    editMode,
    errorsMap,
    onSubmit,
    state,
    disableFields,
    enableButton,
    medicalEventList
  } = usePatientMedicalEventAction()

  const { patientId } = useParams<{ patientId: string }>()
  const [showImageModal, setShowImageModal] = React.useState(false)

  return (
    <StyledBox>
      <Grid container>
        <Grid size={12} paddingBlock={2}>
          <NavLinkBack
            to={`/${PATIENT_MEDICAL_EVENTS(patientId)}`}
            title='Eventos Médicos'
          />
        </Grid>
      </Grid>
      <Typography variant='h5' padding={'20px 0px'}>
        {!editMode ? 'Cadastro de evento médico' : 'Alterar evento médico'}
      </Typography>

      <Grid size={12} marginBottom={4}>
        <Alert variant='outlined' color='warning' severity='info'>
          Os campos marcados com * são obrigatórios
        </Alert>
      </Grid>
      <Grid container spacing={4}>
        <Grid direction={'column'} container spacing={0} size={12}>
          <DateTimePicker
            label='Data e horário do evento *'
            value={
              typeof data.event_date === 'string'
                ? dayjs(data.event_date)
                : (data.event_date ?? null)
            }
            maxDateTime={dayjs()}
            onChange={value => change('event_date', value)}
            disabled={disableFields}
          />
          {errorsMap.has('event_date') && (
            <Typography color='error' variant='caption'>
              {errorsMap.get('event_date')}
            </Typography>
          )}
        </Grid>
        <SelectBox
          label='Evento médico'
          menuItems={medicalEventList}
          required
          control={[
            data.medical_event_id ?? '',
            value => change('medical_event_id', value)
          ]}
          error={errorsMap.has('medical_event_id')}
          helperText={errorsMap.get('medical_event_id')}
        />

        <Grid container direction={'row'} wrap='nowrap' spacing={1} size={12}>
          <SelectBox
            label='Clavien-Dindo'
            menuItems={Object.entries(clavienDindoOptions).map(
              ([value, label]) => ({ value, label })
            )}
            required
            control={[
              data.clavien_dindo ?? '',
              value => change('clavien_dindo', value)
            ]}
            error={errorsMap.has('clavien_dindo')}
            helperText={errorsMap.get('clavien_dindo')}
          />
          <Tooltip title='Ver imagem explicativa'>
            <IconButton
              onClick={() => setShowImageModal(true)}
              disabled={disableFields}
              aria-label='Informações Clavien-Dindo'
            >
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        <InputBase
          label='Descrição'
          variant='outlined'
          value={data.description ?? ''}
          multiline
          rows={4}
          onChange={e => change('description', e.target.value)}
          disabled={disableFields}
          error={errorsMap.has('description')}
          helperText={errorsMap.get('description')}
        />
      </Grid>
      <Grid size={12} justifyContent={'end'} container>
        <ButtonBase
          variant='contained'
          type='submit'
          sx={{ mt: 2 }}
          disabled={!enableButton || state == 'loading'}
          loading={state == 'loading'}
          onClick={onSubmit}
        >
          {editMode ? 'Editar evento médico' : 'Cadastrar evento médico'}
        </ButtonBase>
      </Grid>

      <Modal
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <img
          alt={'Clavien-Dindo Information'}
          src={claviendDindoImage}
          style={{ maxWidth: '70vw', maxHeight: '80vh', margin: '0 auto' }}
        />
      </Modal>
    </StyledBox>
  )
}

export default PatientMedicalEventActionPage
